import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import config from '../../config';
import {
  Users, Phone, Clock, TrendingUp, MousePointerClick,
  Smartphone, FileText, Activity
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

function KpiCard({ label, value, icon: Icon, accent = '#f37721', sublabel }) {
  return (
    <div
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
      className="bg-white rounded-xl p-5 shadow-sm border border-[#673c96]/10 relative overflow-hidden"
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#673c96]/70" style={{ fontFamily: 'Montserrat' }}>
          {label}
        </span>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${accent}12` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <div className="text-3xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>{value}</div>
      {sublabel && <div className="text-[11px] text-[#673c96]/60 mt-1" style={{ fontFamily: 'Montserrat' }}>{sublabel}</div>}
    </div>
  );
}

function ChartCard({ title, icon: Icon, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl p-5 shadow-sm border border-[#673c96]/10">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="w-4 h-4 text-[#673c96]" />}
        <h3 className="text-sm font-semibold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

const DEVICE_COLOR = { desktop: '#311b52', mobile: '#f37721', tablet: '#C5A059' };

function formatDuration(sec) {
  if (!sec) return '0s';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Analytics() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    const fetchData = async () => {
      try {
        const { data } = await axios.get(`${config.API}/admin/analytics-data`, {
          headers: getAuthHeaders(),
          
        });
        if (alive) { setData(data); setError(''); }
      } catch (e) {
        if (alive) setError(e?.response?.status === 403 ? 'Super admin access required.' : 'Failed to load analytics.');
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchData();
    const iv = setInterval(fetchData, 60000); // auto-refresh every 60s
    return () => { alive = false; clearInterval(iv); };
  }, [getAuthHeaders]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="analytics-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#673c96] border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="analytics-error" className="bg-white rounded-xl p-8 text-center border border-[#673c96]/10">
        <p className="text-sm text-[#311b52]" style={{ fontFamily: 'Poppins' }}>{error}</p>
      </div>
    );
  }

  const d = data || {};
  const traffic = d.traffic_7d || [];
  const topPages = d.top_pages || [];
  const devices = d.devices || [];
  const clicks = d.clicks || [];
  const journeys = d.journeys || [];
  const totalDeviceViews = devices.reduce((a, b) => a + (b.count || 0), 0) || 1;

  return (
    <div data-testid="analytics-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-[#311b52]" style={{ fontFamily: 'Poppins' }}>
            Visitor Analytics
          </h1>
          <p className="text-xs text-[#673c96]/70 mt-1" style={{ fontFamily: 'Montserrat' }}>
            GDPR-compliant, IP-anonymized. First-party data only. Auto-refreshes every minute.
          </p>
        </div>
        <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#22c55e]/10 text-[#166534] text-[10px] font-semibold uppercase tracking-wider" style={{ fontFamily: 'Montserrat' }}>
          <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse" /> Live
        </span>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard label="Total Visitors" value={d.total_visitors ?? 0} icon={Users} accent="#311b52" sublabel="Unique anonymized IPs" />
        <KpiCard label="Today's Leads" value={d.todays_leads ?? 0} icon={Phone} accent="#f37721" sublabel="Callback requests today" />
        <KpiCard label="Avg Time on Site" value={formatDuration(d.avg_duration)} icon={Clock} accent="#C5A059" sublabel="Across all sessions" />
      </div>

      {/* Traffic Chart */}
      <ChartCard title="Traffic — Last 7 Days" icon={TrendingUp} testId="chart-traffic-7d">
        <div className="h-64" data-testid="traffic-chart">
          {traffic.length === 0 ? (
            <div className="flex items-center justify-center h-full text-xs text-[#673c96]/50" style={{ fontFamily: 'Montserrat' }}>
              No data yet — traffic will appear after the first page views.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={traffic} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f3f7" vertical={false} />
                <XAxis dataKey="date" stroke="#673c96" fontSize={11} />
                <YAxis stroke="#673c96" fontSize={11} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: '#f5f3f7' }}
                  contentStyle={{ borderRadius: 8, border: '1px solid #673c9620', fontSize: 12 }}
                  labelStyle={{ color: '#311b52', fontFamily: 'Poppins' }}
                />
                <Bar dataKey="views" fill="#311b52" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </ChartCard>

      {/* Two-column */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Top Pages */}
        <ChartCard title="Top Pages" icon={FileText} testId="chart-top-pages">
          <div className="space-y-2.5" data-testid="top-pages-list">
            {topPages.length === 0 && <p className="text-xs text-[#673c96]/50" style={{ fontFamily: 'Montserrat' }}>No page views recorded yet.</p>}
            {topPages.map((p, i) => {
              const max = topPages[0]?.views || 1;
              const pct = Math.max(4, (p.views / max) * 100);
              return (
                <div key={p.page || `page-${i}`} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-[#311b52] w-32 truncate" style={{ fontFamily: 'Montserrat' }} title={p.page}>
                    {p.page || '/'}
                  </span>
                  <div className="flex-1 h-2 bg-[#f5f3f7] rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#311b52] to-[#673c96] rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-[#673c96] w-10 text-right" style={{ fontFamily: 'Poppins' }}>{p.views}</span>
                </div>
              );
            })}
          </div>
        </ChartCard>

        {/* Device Breakdown */}
        <ChartCard title="Device Breakdown" icon={Smartphone} testId="chart-devices">
          <div className="space-y-3" data-testid="devices-list">
            {devices.length === 0 && <p className="text-xs text-[#673c96]/50" style={{ fontFamily: 'Montserrat' }}>No device data yet.</p>}
            {devices.map((dv, i) => {
              const pct = Math.round((dv.count / totalDeviceViews) * 100);
              const color = DEVICE_COLOR[dv.device] || '#673c96';
              return (
                <div key={dv.device || `device-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-[#311b52] capitalize" style={{ fontFamily: 'Poppins' }}>{dv.device}</span>
                    <span className="text-xs text-[#673c96]" style={{ fontFamily: 'Montserrat' }}>{dv.count} ({pct}%)</span>
                  </div>
                  <div className="h-2 bg-[#f5f3f7] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </ChartCard>
      </div>

      {/* Button Clicks */}
      <ChartCard title="Button Clicks" icon={MousePointerClick} testId="chart-clicks">
        <div className="h-48" data-testid="clicks-chart">
          {clicks.length === 0 ? (
            <div className="flex items-center justify-center h-full text-xs text-[#673c96]/50" style={{ fontFamily: 'Montserrat' }}>
              No button clicks tracked yet. "Request Callback" and Social icons are being tracked.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={clicks} layout="vertical" margin={{ top: 5, right: 20, left: 60, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f3f7" horizontal={false} />
                <XAxis type="number" stroke="#673c96" fontSize={11} allowDecimals={false} />
                <YAxis type="category" dataKey="button" stroke="#673c96" fontSize={11} width={120} />
                <Tooltip
                  cursor={{ fill: '#f5f3f7' }}
                  contentStyle={{ borderRadius: 8, border: '1px solid #673c9620', fontSize: 12 }}
                />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {clicks.map((_, i) => <Cell key={i} fill={i === 0 ? '#f37721' : '#C5A059'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </ChartCard>

      {/* Recent Journeys */}
      <ChartCard title="Recent Customer Journeys" icon={Activity} testId="chart-journeys">
        <div className="divide-y divide-[#673c96]/8" data-testid="journeys-list">
          {journeys.length === 0 && (
            <p className="text-xs text-[#673c96]/50 py-2" style={{ fontFamily: 'Montserrat' }}>
              No visitor journeys recorded yet.
            </p>
          )}
          {journeys.map((j, i) => (
            <div key={j.visitor || `visitor-${i}`} className="py-3 flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-[#311b52]/8 flex items-center justify-center shrink-0">
                <Users className="w-3.5 h-3.5 text-[#311b52]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-xs font-semibold text-[#311b52] font-mono" style={{ fontFamily: 'Montserrat' }}>
                    {j.visitor}
                  </span>
                  <span className="text-[10px] text-[#673c96]/60 shrink-0" style={{ fontFamily: 'Montserrat' }}>
                    {relTime(j.first_seen)}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  {(j.pages || []).map((p, idx) => (
                    <span key={`${j.visitor}-page-${idx}`} className="inline-flex items-center">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f5f3f7] text-[#673c96] font-medium" style={{ fontFamily: 'Montserrat' }}>
                        {p}
                      </span>
                      {idx < (j.pages.length - 1) && <span className="text-[#C5A059] mx-1">›</span>}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ChartCard>
    </div>
  );
}
